import { NodeUtils } from 'Editor/services/DataManager';
import { StylesUtils } from 'Editor/services/Edition_v2/Managers/Styles/StylesUtils';

type BlockDataMapper = {
  [style in Editor.Edition.BlockStyles | Editor.Edition.OtherBlockStyles]: {
    getStylePropPath: (blockData: Editor.Data.Node.Data) => Realtime.Core.RealtimePath | undefined;
    processValue: (value: any) => Editor.Edition.StylesMap[style] | undefined;
  };
};

type InlineDataMapper = {
  [style in Editor.Edition.InlineStyles]: {
    hasStyle: (formatData: Editor.Data.Node.FormatData) => boolean;
    hasStyleValue: (
      formatData: Editor.Data.Node.FormatData,
      value: Editor.Edition.StylesMap[style],
    ) => boolean;
    removeStyle: (formatData: Editor.Data.Node.FormatData) => void;
    addStyleValue: (
      formatData: Editor.Data.Node.FormatData,
      value: Editor.Edition.StylesMap[style],
    ) => void;
    getStylePropertyPath: () => Realtime.Core.RealtimePath;
    processValue: (value: any) => Editor.Edition.StylesMap[style] | undefined;
  };
};

type DocumentStylesPropsMapper = {
  [index in Editor.Edition.DocumentStyles]: {
    map: (
      contentStyles: Editor.Edition.AggregatedStylesMap,
      properties: Editor.Data.Structure.DocumentStyleProperties,
    ) => void;
    processValue: (value: any) => Editor.Edition.StylesMap[index] | undefined;
  };
};

export class StylesDataMapper {
  static DocumentStyleProperties: DocumentStylesPropsMapper = {
    fontFamily: {
      map: function (
        contentStyles: Editor.Edition.AggregatedStylesMap,
        properties: Editor.Data.Structure.DocumentStyleProperties,
      ): void {
        if (contentStyles.fontFamily?.length) {
          properties.fontfamily = this.processValue(contentStyles.fontFamily[0]);
        }
      },
      processValue: function (value: any): Editor.Edition.StylesMap['fontFamily'] {
        if (typeof value === 'string') {
          return value;
        }
        return undefined;
      },
    },
    // fontfamily: {
    //   map: function (
    //     contentStyles: Editor.Edition.AggregatedStylesMap,
    //     properties: Editor.Data.Structure.DocumentStyleProperties,
    //   ): void {
    //     if (contentStyles.fontfamily?.length) {
    //       properties.fontfamily = this.processValue(contentStyles.fontfamily[0]);
    //     }
    //   },
    //   processValue: function (value: any): Editor.Edition.StylesMap['fontfamily'] {
    //     if (typeof value === 'string') {
    //       return value;
    //     }
    //     return undefined;
    //   },
    // },
    fontSize: {
      map: function (
        contentStyles: Editor.Edition.AggregatedStylesMap,
        properties: Editor.Data.Structure.DocumentStyleProperties,
      ): void {
        if (contentStyles.fontSize?.length) {
          properties.fontsize = this.processValue(contentStyles.fontSize[0]);
        }
      },
      processValue: function (value: any): Editor.Edition.StylesMap['fontSize'] {
        if (!isNaN(value)) {
          return value;
        }
        return undefined;
      },
    },
    // fontsize: {
    //   map: function (
    //     contentStyles: Editor.Edition.AggregatedStylesMap,
    //     properties: Editor.Data.Structure.DocumentStyleProperties,
    //   ): void {
    //     if (contentStyles.fontsize?.length) {
    //       properties.fontsize = this.processValue(contentStyles.fontsize[0]);
    //     }
    //   },
    //   processValue: function (value: any): Editor.Edition.StylesMap['fontsize'] {
    //     if (!isNaN(value)) {
    //       return value;
    //     }
    //     return undefined;
    //   },
    // },
    color: {
      map: function (
        contentStyles: Editor.Edition.AggregatedStylesMap,
        properties: Editor.Data.Structure.DocumentStyleProperties,
      ): void {
        if (contentStyles.color?.length) {
          properties.color = this.processValue(contentStyles.color[0]);
        }
      },
      processValue: function (value: any): Editor.Edition.StylesMap['color'] {
        return NodeUtils.processColor(value);
      },
    },
    highlightColor: {
      map: function (
        contentStyles: Editor.Edition.AggregatedStylesMap,
        properties: Editor.Data.Structure.DocumentStyleProperties,
      ): void {
        if (contentStyles.highlightColor?.length) {
          properties.bg = this.processValue(contentStyles.highlightColor[0]);
        }
      },
      processValue: function (value: any): Editor.Edition.StylesMap['highlightColor'] {
        return NodeUtils.processColor(value);
      },
    },
    paragraphColor: {
      map: function (
        contentStyles: Editor.Edition.AggregatedStylesMap,
        properties: Editor.Data.Structure.DocumentStyleProperties,
      ): void {
        if (contentStyles.paragraphColor?.length && !contentStyles.highlightColor?.length) {
          properties.bg = this.processValue(contentStyles.paragraphColor[0]);
        }
      },
      processValue: function (value: any): Editor.Edition.StylesMap['paragraphColor'] {
        return NodeUtils.processColor(value);
      },
    },
    bold: {
      map: function (
        contentStyles: Editor.Edition.AggregatedStylesMap,
        properties: Editor.Data.Structure.DocumentStyleProperties,
      ): void {
        if (contentStyles.bold?.length) {
          properties.bold = this.processValue(contentStyles.bold[0]);
        }
      },
      processValue: function (value: any): Editor.Edition.StylesMap['bold'] {
        if (typeof value === 'boolean') {
          return value;
        }
        return undefined;
      },
    },
    italic: {
      map: function (
        contentStyles: Editor.Edition.AggregatedStylesMap,
        properties: Editor.Data.Structure.DocumentStyleProperties,
      ): void {
        if (contentStyles.italic?.length) {
          properties.italic = this.processValue(contentStyles.italic[0]);
        }
      },
      processValue: function (value: any): Editor.Edition.StylesMap['italic'] | undefined {
        if (typeof value === 'boolean') {
          return value;
        }
        return undefined;
      },
    },
    underline: {
      map: function (
        contentStyles: Editor.Edition.AggregatedStylesMap,
        properties: Editor.Data.Structure.DocumentStyleProperties,
      ): void {
        if (contentStyles.underline?.length) {
          properties.underline = this.processValue(contentStyles.underline[0]);
        }
      },
      processValue: function (value: any): Editor.Edition.StylesMap['underline'] | undefined {
        if (typeof value === 'boolean') {
          return value;
        }
        return undefined;
      },
    },
    alignment: {
      map: function (
        contentStyles: Editor.Edition.AggregatedStylesMap,
        properties: Editor.Data.Structure.DocumentStyleProperties,
      ): void {
        if (contentStyles.alignment?.length) {
          // @ts-expect-error generic type issue
          properties.a = this.processValue(contentStyles.alignment[0]);
        }
      },
      processValue: function (value: any): Editor.Data.Node.Alignment | undefined {
        return NodeUtils.processAlignment(value);
      },
    },
    lineHeight: {
      map: function (
        contentStyles: Editor.Edition.AggregatedStylesMap,
        properties: Editor.Data.Structure.DocumentStyleProperties,
      ): void {
        if (contentStyles.lineHeight?.length) {
          properties.lh = this.processValue(contentStyles.lineHeight[0]);
        }
      },
      processValue: function (value: any): Editor.Edition.StylesMap['lineHeight'] | undefined {
        if (!isNaN(value)) {
          return value;
        }
        return undefined;
      },
    },
    lineRule: {
      map: function (
        contentStyles: Editor.Edition.AggregatedStylesMap,
        properties: Editor.Data.Structure.DocumentStyleProperties,
      ): void {
        if (contentStyles.lineRule?.length) {
          properties.lr = this.processValue(contentStyles.lineRule[0]);
        }
      },
      processValue: function (value: any): Editor.Edition.StylesMap['lineRule'] | undefined {
        if (typeof value === 'string') {
          return value;
        }
        return undefined;
      },
    },
    spaceBefore: {
      map: function (
        contentStyles: Editor.Edition.AggregatedStylesMap,
        properties: Editor.Data.Structure.DocumentStyleProperties,
      ): void {
        if (contentStyles.spaceBefore?.length) {
          properties.sb = this.processValue(contentStyles.spaceBefore[0]);
        }
      },
      processValue: function (value: any): Editor.Edition.StylesMap['spaceBefore'] | undefined {
        if (!isNaN(value)) {
          return value;
        }
        return undefined;
      },
    },
    spaceAfter: {
      map: function (
        contentStyles: Editor.Edition.AggregatedStylesMap,
        properties: Editor.Data.Structure.DocumentStyleProperties,
      ): void {
        if (contentStyles.spaceAfter?.length) {
          properties.sa = this.processValue(contentStyles.spaceAfter[0]);
        }
      },
      processValue: function (value: any): Editor.Edition.StylesMap['spaceAfter'] | undefined {
        if (!isNaN(value)) {
          return value;
        }
        return undefined;
      },
    },
    leftIndentation: {
      map: function (
        contentStyles: Editor.Edition.AggregatedStylesMap,
        properties: Editor.Data.Structure.DocumentStyleProperties,
      ): void {
        if (contentStyles.leftIndentation?.length) {
          if (!properties.ind) {
            properties.ind = {
              l: this.processValue(contentStyles.leftIndentation[0]),
            };
          } else {
            properties.ind.l = this.processValue(contentStyles.leftIndentation[0]);
          }
        }
      },
      processValue: function (value: any): Editor.Edition.StylesMap['leftIndentation'] | undefined {
        if (!isNaN(value)) {
          return value;
        }
        return undefined;
      },
    },
    rightIndentation: {
      map: function (
        contentStyles: Editor.Edition.AggregatedStylesMap,
        properties: Editor.Data.Structure.DocumentStyleProperties,
      ): void {
        if (contentStyles.rightIndentation?.length) {
          if (!properties.ind) {
            properties.ind = {
              r: this.processValue(contentStyles.rightIndentation[0]),
            };
          } else {
            properties.ind.r = this.processValue(contentStyles.rightIndentation[0]);
          }
        }
      },
      processValue: function (
        value: any,
      ): Editor.Edition.StylesMap['rightIndentation'] | undefined {
        if (!isNaN(value)) {
          return value;
        }
        return undefined;
      },
    },
    specialIndent: {
      map: function (
        contentStyles: Editor.Edition.AggregatedStylesMap,
        properties: Editor.Data.Structure.DocumentStyleProperties,
      ): void {
        if (contentStyles.specialIndent?.length && contentStyles.specialIndentValue?.length) {
          if (!properties.sp_ind) {
            properties.sp_ind = {
              t: this.processValue(contentStyles.specialIndent[0]),
            };
          } else {
            properties.sp_ind.t = this.processValue(contentStyles.specialIndent[0]);
          }
        }
      },
      processValue: function (value: any): Editor.Edition.StylesMap['specialIndent'] | undefined {
        if (typeof value === 'string' && (value === 'f' || value === 'h' || value === 'none')) {
          return value;
        }
        return undefined;
      },
    },
    specialIndentValue: {
      map: function (
        contentStyles: Editor.Edition.AggregatedStylesMap,
        properties: Editor.Data.Structure.DocumentStyleProperties,
      ): void {
        if (contentStyles.specialIndentValue?.length) {
          if (!properties.sp_ind) {
            properties.sp_ind = {
              v: this.processValue(contentStyles.specialIndentValue[0]),
            };
          } else {
            properties.sp_ind.v = this.processValue(contentStyles.specialIndentValue[0]);
          }
        }
      },
      processValue: function (
        value: any,
      ): Editor.Edition.StylesMap['specialIndentValue'] | undefined {
        if (!isNaN(value)) {
          return value;
        }
        return undefined;
      },
    },
    vanish: {
      map: function (
        contentStyles: Editor.Edition.AggregatedStylesMap,
        properties: Editor.Data.Structure.DocumentStyleProperties,
      ): void {
        if (contentStyles.vanish?.length) {
          properties.v = this.processValue(contentStyles.vanish[0]);
        }
      },
      processValue: function (value: any): Editor.Edition.StylesMap['vanish'] | undefined {
        if (typeof value === 'boolean') {
          return value;
        }
        return undefined;
      },
    },
    listId: {
      map: function (
        contentStyles: Editor.Edition.AggregatedStylesMap,
        properties: Editor.Data.Structure.DocumentStyleProperties,
      ): void {
        if (contentStyles.listId?.length) {
          const listId = this.processValue(contentStyles.listId[0]);
          if (!properties.lst) {
            properties.lst = {
              lId: listId,
            };
          } else {
            properties.lst.lId = listId;
          }
        }
      },
      processValue: function (value: any): Editor.Edition.StylesMap['listId'] | undefined {
        return value;
      },
    },
    listLevel: {
      map: function (
        contentStyles: Editor.Edition.AggregatedStylesMap,
        properties: Editor.Data.Structure.DocumentStyleProperties,
      ): void {
        if (contentStyles.listLevel?.length) {
          const listLevel = this.processValue(contentStyles.listLevel[0]);
          if (!properties.lst) {
            properties.lst = {
              lLv: listLevel,
            };
          } else {
            properties.lst.lLv = listLevel;
          }
        }
      },
      processValue: function (value: any): Editor.Edition.StylesMap['listLevel'] {
        return value;
      },
    },
    asb: {
      map: function (
        contentStyles: Editor.Edition.AggregatedStylesMap,
        properties: Editor.Data.Structure.DocumentStyleProperties,
      ): void {
        if (contentStyles.asb?.length) {
          properties.asb = this.processValue(contentStyles.asb[0]);
        }
      },
      processValue: function (value: any): Editor.Edition.StylesMap['asb'] | undefined {
        if (typeof value === 'boolean') {
          return value;
        }
        return undefined;
      },
    },
    asa: {
      map: function (
        contentStyles: Editor.Edition.AggregatedStylesMap,
        properties: Editor.Data.Structure.DocumentStyleProperties,
      ): void {
        if (contentStyles.asa?.length) {
          properties.asa = this.processValue(contentStyles.asa[0]);
        }
      },
      processValue: function (value: any): Editor.Edition.StylesMap['asa'] | undefined {
        if (typeof value === 'boolean') {
          return value;
        }
        return undefined;
      },
    },
    wc: {
      map: function (
        contentStyles: Editor.Edition.AggregatedStylesMap,
        properties: Editor.Data.Structure.DocumentStyleProperties,
      ): void {
        if (contentStyles.wc?.length) {
          properties.wc = this.processValue(contentStyles.wc[0]);
        }
      },
      processValue: function (value: any): Editor.Edition.StylesMap['wc'] | undefined {
        if (typeof value === 'boolean') {
          return value;
        }
        return undefined;
      },
    },
    kn: {
      map: function (
        contentStyles: Editor.Edition.AggregatedStylesMap,
        properties: Editor.Data.Structure.DocumentStyleProperties,
      ): void {
        if (contentStyles.kn?.length) {
          properties.kn = this.processValue(contentStyles.kn[0]);
        }
      },
      processValue: function (value: any): Editor.Edition.StylesMap['kn'] | undefined {
        if (typeof value === 'boolean') {
          return value;
        }
        return undefined;
      },
    },
    kl: {
      map: function (
        contentStyles: Editor.Edition.AggregatedStylesMap,
        properties: Editor.Data.Structure.DocumentStyleProperties,
      ): void {
        if (contentStyles.kl?.length) {
          properties.kl = this.processValue(contentStyles.kl[0]);
        }
      },
      processValue: function (value: any): Editor.Edition.StylesMap['kl'] | undefined {
        if (typeof value === 'boolean') {
          return value;
        }
        return undefined;
      },
    },
    cts: {
      map: function (
        contentStyles: Editor.Edition.AggregatedStylesMap,
        properties: Editor.Data.Structure.DocumentStyleProperties,
      ): void {
        if (contentStyles.cts?.length) {
          properties.cts = this.processValue(contentStyles.cts[0]);
        }
      },
      processValue: function (value: any): Editor.Edition.StylesMap['cts'] | undefined {
        if (typeof value === 'boolean') {
          return value;
        }
        return undefined;
      },
    },
    pbb: {
      map: function (
        contentStyles: Editor.Edition.AggregatedStylesMap,
        properties: Editor.Data.Structure.DocumentStyleProperties,
      ): void {
        if (contentStyles.pbb?.length) {
          properties.pbb = this.processValue(contentStyles.pbb[0]);
        }
      },
      processValue: function (value: any): Editor.Edition.StylesMap['pbb'] | undefined {
        if (typeof value === 'boolean') {
          return value;
        }
        return undefined;
      },
    },
    otl: {
      map: function (
        contentStyles: Editor.Edition.AggregatedStylesMap,
        properties: Editor.Data.Structure.DocumentStyleProperties,
      ): void {
        if (contentStyles.otl?.length) {
          properties.otl = this.processValue(contentStyles.otl[0]);
        }
      },
      processValue: function (value: any): Editor.Edition.StylesMap['otl'] | undefined {
        if (!isNaN(value)) {
          return value;
        }
        return undefined;
      },
    },
    tabs: {
      map: function (
        contentStyles: Editor.Edition.AggregatedStylesMap,
        properties: Editor.Data.Structure.DocumentStyleProperties,
      ): void {
        if (contentStyles.tabs?.length) {
          properties.tabs = this.processValue(contentStyles.tabs[0]);
        }
      },
      processValue: function (value: any): Editor.Edition.StylesMap['tabs'] | undefined {
        return value;
      },
    },
  };

  static BlockData: BlockDataMapper = {
    fontFamily: {
      getStylePropPath: function (blockData: Editor.Data.Node.Data) {
        if (NodeUtils.isParagraphData(blockData)) {
          return ['properties', 'ff'];
        }
        return undefined;
      },
      processValue: function (value: any): Editor.Edition.StylesMap['fontFamily'] {
        return value;
      },
    },
    fontSize: {
      getStylePropPath: function (blockData: Editor.Data.Node.Data) {
        if (NodeUtils.isParagraphData(blockData)) {
          return ['properties', 'fs'];
        }
        return undefined;
      },
      processValue: function (value: any): Editor.Edition.StylesMap['fontSize'] {
        return value;
      },
    },
    color: {
      getStylePropPath: function (blockData: Editor.Data.Node.Data) {
        if (NodeUtils.isParagraphData(blockData)) {
          return ['properties', 'c'];
        }
        return undefined;
      },
      processValue: function (value: any): Editor.Edition.StylesMap['color'] {
        return NodeUtils.processColor(value);
      },
    },
    highlightColor: {
      getStylePropPath: function (blockData: Editor.Data.Node.Data) {
        if (NodeUtils.isParagraphData(blockData)) {
          return ['properties', 'hc'];
        }
        return undefined;
      },
      processValue: function (value: any): Editor.Edition.StylesMap['highlightColor'] {
        return NodeUtils.processColor(value);
      },
    },
    bold: {
      getStylePropPath: function (blockData: Editor.Data.Node.Data) {
        if (NodeUtils.isParagraphData(blockData)) {
          return ['properties', 'b'];
        }
        return undefined;
      },
      processValue: function (value: any): Editor.Edition.StylesMap['bold'] {
        return value;
      },
    },
    italic: {
      getStylePropPath: function (blockData: Editor.Data.Node.Data) {
        if (NodeUtils.isParagraphData(blockData)) {
          return ['properties', 'i'];
        }
        return undefined;
      },
      processValue: function (value: any): Editor.Edition.StylesMap['italic'] {
        return value;
      },
    },
    underline: {
      getStylePropPath: function (blockData: Editor.Data.Node.Data) {
        if (NodeUtils.isParagraphData(blockData)) {
          return ['properties', 'u'];
        }
        return undefined;
      },
      processValue: function (value: any): Editor.Edition.StylesMap['underline'] {
        return value;
      },
    },
    strikethrough: {
      getStylePropPath: function (blockData: Editor.Data.Node.Data) {
        if (NodeUtils.isParagraphData(blockData)) {
          return ['properties', 'strikethrough'];
        }
        return undefined;
      },
      processValue: function (value: any): Editor.Edition.StylesMap['strikethrough'] {
        return value;
      },
    },
    superscript: {
      getStylePropPath: function (blockData: Editor.Data.Node.Data) {
        if (NodeUtils.isParagraphData(blockData)) {
          return ['properties', 'sps'];
        }
        return undefined;
      },
      processValue: function (value: any): Editor.Edition.StylesMap['subscript'] {
        return value;
      },
    },
    subscript: {
      getStylePropPath: function (blockData: Editor.Data.Node.Data) {
        if (NodeUtils.isParagraphData(blockData)) {
          return ['properties', 'sbs'];
        }
        return undefined;
      },
      processValue: function (value: any): Editor.Edition.StylesMap['subscript'] {
        return value;
      },
    },
    vanish: {
      getStylePropPath: function (blockData: Editor.Data.Node.Data) {
        return ['properties', 'v'];
      },
      processValue: function (value: any): Editor.Edition.StylesMap['vanish'] {
        return value;
      },
    },
    paragraphColor: {
      getStylePropPath: function (blockData: Editor.Data.Node.Data) {
        if (NodeUtils.isParagraphData(blockData)) {
          return ['properties', 'bg'];
        }
        return undefined;
      },
      processValue: function (value: any): Editor.Edition.StylesMap['paragraphColor'] {
        return NodeUtils.processColor(value);
      },
    },
    alignment: {
      getStylePropPath: function (blockData: Editor.Data.Node.Data) {
        if (NodeUtils.isParagraphData(blockData)) {
          return ['properties', 'a'];
        }
        return undefined;
      },
      processValue: function (value: any): Editor.Edition.StylesMap['alignment'] {
        return NodeUtils.processAlignment(value);
      },
    },
    lineHeight: {
      getStylePropPath: function (blockData: Editor.Data.Node.Data) {
        if (NodeUtils.isParagraphData(blockData)) {
          return ['properties', 'lh'];
        }
        return undefined;
      },
      processValue: function (value: any): Editor.Edition.StylesMap['lineHeight'] {
        return value;
      },
    },
    lineRule: {
      getStylePropPath: function (blockData: Editor.Data.Node.Data) {
        if (NodeUtils.isParagraphData(blockData)) {
          return ['properties', 'lr'];
        }
        return undefined;
      },
      processValue: function (value: any): Editor.Edition.StylesMap['lineRule'] {
        return value;
      },
    },
    spaceBefore: {
      getStylePropPath: function (blockData: Editor.Data.Node.Data) {
        if (NodeUtils.isParagraphData(blockData)) {
          return ['properties', 'sb'];
        }
        return undefined;
      },
      processValue: function (value: any): Editor.Edition.StylesMap['spaceBefore'] {
        return value;
      },
    },
    spaceAfter: {
      getStylePropPath: function (blockData: Editor.Data.Node.Data) {
        if (NodeUtils.isParagraphData(blockData)) {
          return ['properties', 'sa'];
        }
        return undefined;
      },
      processValue: function (value: any): Editor.Edition.StylesMap['spaceAfter'] {
        return value;
      },
    },
    leftIndentation: {
      getStylePropPath: function (blockData: Editor.Data.Node.Data) {
        if (NodeUtils.isParagraphData(blockData)) {
          return ['properties', 'ind', 'l'];
        }
        return undefined;
      },
      processValue: function (value: any): Editor.Edition.StylesMap['leftIndentation'] {
        if (!isNaN(value)) {
          if (value > 0) {
            return Math.min(StylesUtils.INDENTATION_LIMITS.LEFT.MANIPULATION_MAX, value);
          } else {
            return Math.max(StylesUtils.INDENTATION_LIMITS.LEFT.MANIPULATION_MIN, value);
          }
        }
        return undefined;
      },
    },
    rightIndentation: {
      getStylePropPath: function (blockData: Editor.Data.Node.Data) {
        if (NodeUtils.isParagraphData(blockData)) {
          return ['properties', 'ind', 'r'];
        }
        return undefined;
      },
      processValue: function (value: any): Editor.Edition.StylesMap['rightIndentation'] {
        if (!isNaN(value)) {
          if (value > 0) {
            return Math.min(StylesUtils.INDENTATION_LIMITS.RIGHT.MANIPULATION_MAX, value);
          } else {
            return Math.max(StylesUtils.INDENTATION_LIMITS.RIGHT.MANIPULATION_MIN, value);
          }
        }
        return undefined;
      },
    },
    specialIndent: {
      getStylePropPath: function (blockData: Editor.Data.Node.Data) {
        if (NodeUtils.isParagraphData(blockData)) {
          return ['properties', 'sp_ind', 't'];
        }
        return undefined;
      },
      processValue: function (value: any): Editor.Edition.StylesMap['specialIndent'] {
        if (value != null) {
          return value;
        }
        return undefined;
      },
    },
    specialIndentValue: {
      getStylePropPath: function (blockData: Editor.Data.Node.Data) {
        if (NodeUtils.isParagraphData(blockData)) {
          return ['properties', 'sp_ind', 'v'];
        }
        return undefined;
      },
      processValue: function (value: any): Editor.Edition.StylesMap['specialIndentValue'] {
        if (!isNaN(value)) {
          if (value > 0) {
            return Math.min(StylesUtils.INDENTATION_LIMITS.SPECIAL_INDENT.MANIPULATION_MAX, value);
          } else {
            return Math.max(StylesUtils.INDENTATION_LIMITS.SPECIAL_INDENT.MANIPULATION_MIN, value);
          }
        }
        return undefined;
      },
    },
    listId: {
      getStylePropPath: function (blockData: Editor.Data.Node.Data) {
        // if (NodeUtils.isParagraphData(blockData)) {
        //   return ['properties', 'listId'];
        // }
        return undefined;
      },
      processValue: function (value: any): Editor.Edition.StylesMap['listId'] {
        return value;
      },
    },
    listLevel: {
      getStylePropPath: function (blockData: Editor.Data.Node.Data) {
        // if (NodeUtils.isParagraphData(blockData)) {
        //   return ['properties', 'listLevel'];
        // }
        return undefined;
      },
      processValue: function (value: any): Editor.Edition.StylesMap['listLevel'] {
        return value;
      },
    },
    asb: {
      getStylePropPath: function (blockData: Editor.Data.Node.Data) {
        if (NodeUtils.isParagraphData(blockData)) {
          return ['properties', 'asb'];
        }
        return undefined;
      },
      processValue: function (value: any): Editor.Edition.StylesMap['asb'] {
        return value;
      },
    },
    asa: {
      getStylePropPath: function (blockData: Editor.Data.Node.Data) {
        if (NodeUtils.isParagraphData(blockData)) {
          return ['properties', 'asa'];
        }
        return undefined;
      },
      processValue: function (value: any): Editor.Edition.StylesMap['asa'] {
        return value;
      },
    },
    wc: {
      getStylePropPath: function (blockData: Editor.Data.Node.Data) {
        if (NodeUtils.isParagraphData(blockData)) {
          return ['properties', 'wc'];
        }
        return undefined;
      },
      processValue: function (value: any): Editor.Edition.StylesMap['wc'] {
        return value;
      },
    },
    kn: {
      getStylePropPath: function (blockData: Editor.Data.Node.Data) {
        if (NodeUtils.isParagraphData(blockData)) {
          return ['properties', 'kn'];
        }
        return undefined;
      },
      processValue: function (value: any): Editor.Edition.StylesMap['kn'] {
        return value;
      },
    },
    kl: {
      getStylePropPath: function (blockData: Editor.Data.Node.Data) {
        if (NodeUtils.isParagraphData(blockData)) {
          return ['properties', 'kl'];
        }
        return undefined;
      },
      processValue: function (value: any): Editor.Edition.StylesMap['kl'] {
        return value;
      },
    },
    cts: {
      getStylePropPath: function (blockData: Editor.Data.Node.Data) {
        if (NodeUtils.isParagraphData(blockData)) {
          return ['properties', 'cts'];
        }
        return undefined;
      },
      processValue: function (value: any): Editor.Edition.StylesMap['cts'] {
        return value;
      },
    },
    pbb: {
      getStylePropPath: function (blockData: Editor.Data.Node.Data) {
        if (NodeUtils.isParagraphData(blockData)) {
          return ['properties', 'pbb'];
        }
        return undefined;
      },
      processValue: function (value: any): Editor.Edition.StylesMap['pbb'] {
        return value;
      },
    },
    otl: {
      getStylePropPath: function (blockData: Editor.Data.Node.Data) {
        if (NodeUtils.isParagraphData(blockData)) {
          return ['properties', 'otl'];
        }
        return undefined;
      },
      processValue: function (value: any): Editor.Edition.StylesMap['otl'] {
        return value;
      },
    },
    tabs: {
      getStylePropPath: function (blockData: Editor.Data.Node.Data) {
        if (NodeUtils.isParagraphData(blockData)) {
          return ['properties', 'tabs'];
        }
        return undefined;
      },
      processValue: function (value: any): Editor.Edition.StylesMap['tabs'] {
        return value;
      },
    },
    s: {
      getStylePropPath: function (blockData: Editor.Data.Node.Data) {
        if (NodeUtils.isParagraphData(blockData)) {
          return ['properties', 's'];
        }
        return undefined;
      },
      processValue: function (value: any): Editor.Edition.StylesMap['s'] {
        return value;
      },
    },
  } as const;

  static InlineData: InlineDataMapper = {
    fontFamily: {
      hasStyle: function (formatData: Editor.Data.Node.FormatData): boolean {
        return formatData.properties.fontfamily != null;
      },
      hasStyleValue: function (formatData: Editor.Data.Node.FormatData, value): boolean {
        if (value != null) {
          return this.processValue(formatData.properties.fontfamily) === this.processValue(value);
        }
        return false;
      },
      removeStyle: function (formatData: Editor.Data.Node.FormatData): void {
        delete formatData.properties.fontfamily;
      },
      addStyleValue: function (formatData: Editor.Data.Node.FormatData, value): void {
        const processedValue = this.processValue(value);
        if (processedValue != null) {
          formatData.properties.fontfamily = processedValue;
        }
      },
      getStylePropertyPath: function () {
        return ['properties', 'fontfamily'];
      },
      processValue: function (value: any): Editor.Edition.StylesMap['fontFamily'] {
        return value;
      },
    },
    fontSize: {
      hasStyle: function (formatData: Editor.Data.Node.FormatData): boolean {
        return formatData.properties.fontsize != null;
      },
      hasStyleValue: function (formatData: Editor.Data.Node.FormatData, value): boolean {
        if (value != null) {
          return this.processValue(formatData.properties.fontsize) === this.processValue(value);
        }
        return false;
      },
      removeStyle: function (formatData: Editor.Data.Node.FormatData): void {
        delete formatData.properties.fontsize;
      },
      addStyleValue: function (formatData: Editor.Data.Node.FormatData, value): void {
        const processedValue = this.processValue(value);
        if (processedValue != null) {
          formatData.properties.fontsize = processedValue;
        }
      },
      getStylePropertyPath: function () {
        return ['properties', 'fontsize'];
      },
      processValue: function (value: any): Editor.Edition.StylesMap['fontSize'] {
        return value;
      },
    },
    color: {
      hasStyle: function (formatData: Editor.Data.Node.FormatData): boolean {
        return formatData.properties.color != null;
      },
      hasStyleValue: function (formatData: Editor.Data.Node.FormatData, value): boolean {
        if (value != null) {
          return this.processValue(formatData.properties.color) === this.processValue(value);
        }
        return false;
      },
      removeStyle: function (formatData: Editor.Data.Node.FormatData): void {
        delete formatData.properties.color;
      },
      addStyleValue: function (formatData: Editor.Data.Node.FormatData, value): void {
        const processedValue = this.processValue(value);
        if (processedValue != null) {
          formatData.properties.color = processedValue;
        }
      },
      getStylePropertyPath: function () {
        return ['properties', 'color'];
      },
      processValue: function (value: any): Editor.Edition.StylesMap['color'] {
        return NodeUtils.processColor(value);
      },
    },
    highlightColor: {
      hasStyle: function (formatData: Editor.Data.Node.FormatData): boolean {
        return formatData.properties.backgroundcolor != null;
      },
      hasStyleValue: function (formatData: Editor.Data.Node.FormatData, value): boolean {
        if (value != null) {
          return (
            this.processValue(formatData.properties.backgroundcolor) === this.processValue(value)
          );
        }
        return false;
      },
      removeStyle: function (formatData: Editor.Data.Node.FormatData): void {
        delete formatData.properties.backgroundcolor;
      },
      addStyleValue: function (formatData: Editor.Data.Node.FormatData, value): void {
        const processedValue = this.processValue(value);
        if (processedValue != null) {
          formatData.properties.backgroundcolor = processedValue;
        }
      },
      getStylePropertyPath: function () {
        return ['properties', 'backgroundcolor'];
      },
      processValue: function (value: any): Editor.Edition.StylesMap['highlightColor'] {
        return NodeUtils.processColor(value);
      },
    },
    bold: {
      hasStyle: function (formatData: Editor.Data.Node.FormatData): boolean {
        return formatData.properties.bold != null;
      },
      hasStyleValue: function (formatData: Editor.Data.Node.FormatData, value): boolean {
        if (value != null) {
          return this.processValue(formatData.properties.bold) === this.processValue(value);
        }
        return false;
      },
      removeStyle: function (formatData: Editor.Data.Node.FormatData): void {
        delete formatData.properties.bold;
      },
      addStyleValue: function (formatData: Editor.Data.Node.FormatData, value): void {
        const processedValue = this.processValue(value);
        if (processedValue != null) {
          formatData.properties.bold = processedValue;
        }
      },
      getStylePropertyPath: function () {
        return ['properties', 'bold'];
      },
      processValue: function (value: any): Editor.Edition.StylesMap['bold'] {
        return value;
      },
    },
    italic: {
      hasStyle: function (formatData: Editor.Data.Node.FormatData): boolean {
        return formatData.properties.italic != null;
      },
      hasStyleValue: function (formatData: Editor.Data.Node.FormatData, value): boolean {
        if (value != null) {
          return this.processValue(formatData.properties.italic) === this.processValue(value);
        }
        return false;
      },
      removeStyle: function (formatData: Editor.Data.Node.FormatData): void {
        delete formatData.properties.italic;
      },
      addStyleValue: function (formatData: Editor.Data.Node.FormatData, value): void {
        const processedValue = this.processValue(value);
        if (processedValue != null) {
          formatData.properties.italic = processedValue;
        }
      },
      getStylePropertyPath: function () {
        return ['properties', 'italic'];
      },
      processValue: function (value: any): Editor.Edition.StylesMap['italic'] {
        return value;
      },
    },
    underline: {
      hasStyle: function (formatData: Editor.Data.Node.FormatData): boolean {
        return formatData.properties.underline != null;
      },
      hasStyleValue: function (formatData: Editor.Data.Node.FormatData, value): boolean {
        if (value != null) {
          return this.processValue(formatData.properties.underline) === this.processValue(value);
        }
        return false;
      },
      removeStyle: function (formatData: Editor.Data.Node.FormatData): void {
        delete formatData.properties.underline;
      },
      addStyleValue: function (formatData: Editor.Data.Node.FormatData, value): void {
        const processedValue = this.processValue(value);
        if (processedValue != null) {
          formatData.properties.underline = processedValue;
        }
      },
      getStylePropertyPath: function () {
        return ['properties', 'underline'];
      },
      processValue: function (value: any): Editor.Edition.StylesMap['underline'] {
        return value;
      },
    },
    strikethrough: {
      hasStyle: function (formatData: Editor.Data.Node.FormatData): boolean {
        return formatData.properties.strikethrough != null;
      },
      hasStyleValue: function (formatData: Editor.Data.Node.FormatData, value): boolean {
        if (value != null) {
          return (
            this.processValue(formatData.properties.strikethrough) === this.processValue(value)
          );
        }
        return false;
      },
      removeStyle: function (formatData: Editor.Data.Node.FormatData): void {
        delete formatData.properties.strikethrough;
      },
      addStyleValue: function (formatData: Editor.Data.Node.FormatData, value): void {
        const processedValue = this.processValue(value);
        if (processedValue != null) {
          formatData.properties.strikethrough = processedValue;
        }
      },
      getStylePropertyPath: function () {
        return ['properties', 'strikethrough'];
      },
      processValue: function (value: any): Editor.Edition.StylesMap['strikethrough'] {
        return value;
      },
    },
    superscript: {
      hasStyle: function (formatData: Editor.Data.Node.FormatData): boolean {
        return formatData.properties.superscript != null;
      },
      hasStyleValue: function (formatData: Editor.Data.Node.FormatData, value): boolean {
        if (value != null) {
          return this.processValue(formatData.properties.superscript) === this.processValue(value);
        }
        return false;
      },
      removeStyle: function (formatData: Editor.Data.Node.FormatData): void {
        delete formatData.properties.superscript;
      },
      addStyleValue: function (formatData: Editor.Data.Node.FormatData, value): void {
        const processedValue = this.processValue(value);
        if (processedValue != null) {
          formatData.properties.superscript = processedValue;
        }
      },
      getStylePropertyPath: function () {
        return ['properties', 'superscript'];
      },
      processValue: function (value: any): Editor.Edition.StylesMap['superscript'] {
        return value;
      },
    },
    subscript: {
      hasStyle: function (formatData: Editor.Data.Node.FormatData): boolean {
        return formatData.properties.subscript != null;
      },
      hasStyleValue: function (formatData: Editor.Data.Node.FormatData, value): boolean {
        if (value != null) {
          return this.processValue(formatData.properties.subscript) === this.processValue(value);
        }
        return false;
      },
      removeStyle: function (formatData: Editor.Data.Node.FormatData): void {
        delete formatData.properties.subscript;
      },
      addStyleValue: function (formatData: Editor.Data.Node.FormatData, value): void {
        const processedValue = this.processValue(value);
        if (processedValue != null) {
          formatData.properties.subscript = processedValue;
        }
      },
      getStylePropertyPath: function () {
        return ['properties', 'subscript'];
      },
      processValue: function (value: any): Editor.Edition.StylesMap['subscript'] {
        return value;
      },
    },
    vanish: {
      hasStyle: function (formatData: Editor.Data.Node.FormatData): boolean {
        return formatData.properties.v != null;
      },
      hasStyleValue: function (formatData: Editor.Data.Node.FormatData, value): boolean {
        if (value != null) {
          return this.processValue(formatData.properties.v) === this.processValue(value);
        }
        return false;
      },
      removeStyle: function (formatData: Editor.Data.Node.FormatData): void {
        delete formatData.properties.v;
      },
      addStyleValue: function (formatData: Editor.Data.Node.FormatData, value): void {
        const processedValue = this.processValue(value);
        if (processedValue != null) {
          formatData.properties.v = processedValue;
        }
      },
      getStylePropertyPath: function () {
        return ['properties', 'v'];
      },
      processValue: function (value: any): Editor.Edition.StylesMap['vanish'] {
        return value;
      },
    },
  } as const;
}
